import React, { useState, useRef, useEffect } from "react"
import {
  GlobalStyle,
  AppContainer,
  Main,
  Header,
  useSocketMessages,
  createSocket,
  useHistory,
  useBlurAndAbove,
  useNotification,
  Notification,
  Error,
  Protocol,
  objToStringSettings,
  Loading,
  useLoading,
  settingsStringParsing,
  useGlobalAudio,
  addMouseFlow,
  Settings,
  GameRules,
  ProvablyFair,
  History,
  BetReply,
  SetStake,
} from "@orbital/games-frontend-library"

import { MessagesList } from "./lib/video_poker_msg"
import { colors } from "./lib/colors"
import { useGameStates } from "./components/functions/useGameStates"

import VideoPoker from "./components/VideoPoker"
import useAudio from "./components/functions/useAudioFiles"
import PokerRules from "./components/gameRules/PokerRules"
import Gamble from "./components/gamePlay/gamble/Gamble"
import CustomReply from "./components/betReply/CustomReply"

const App = () => {
  const name = "orbital.videopoker"
  const WS_URL = "{{SERVER_WS_URL}}"
  const wsbridge = "wsbridge/v1/game"
  const enableLoading = false
  const balanceUpdateOn = "now"
  const clickable = true
  const socket = useRef<any>(null)
  const [gameInfo, setgameInfo] = useState({
    user_id: "",
    currency: "",
    balance: "",
    precision: 2,
    minBet: "",
    maxBet: "",
    maxWin: "",
    game_settings: "",
  })

  const settings = [
    { name: "stake", minStake: "", maxStake: "100000000000.00" },
    { name: "music" },
    { name: "sound" },
    { name: "volume" },
  ]
  const { user_id, precision, minBet, maxBet } = gameInfo
  const [seeds, setSeeds] = useState({
    client_seed: "",
    server_seed_SHA256: "",
    next_server_seed_SHA256: "",
    nonce: 0,
    old_client_seed: "",
    old_servr_seed: "",
    old_erver_seed_SHA256: "",
    old_nonce: 0,
  })
  const [stateRound, setStateRound] = useState({
    cards: [],
    balance: 0,
    wintype: 0,
    winCoef: 0,
    doubleupcard: "X0",
    winamount: 0,
    winPositions: [],
    history: [],
    state: 0,
  })
  const [errorCode, setErrorCode] = useState<number>(0)

  const [audio, setaudio] = useState({
    sound: false,
    music: false,
    volume: "0.5",
  })
  const [betReply, setBetReply] = useState({})
  const [history, addInHistory] = useHistory(user_id)

  const [stake, setstake] = useState("")
  const [holded, setHolded] = useState<any[]>([0, 0, 0, 0, 0])
  const [roundId, setRoundId] = useState<string>("")
  const [errorText, seterrorText] = useState<string | boolean>(false)
  const [isLoaded, setisLoaded] = useState(false)

  const [blur, unBlur, settingsUnblur, above, handleAbove] = useBlurAndAbove()
  const { LOCALAUDIO } = useAudio(audio.volume, audio.sound)

  useEffect(() => {
    const newStake = Number(gameInfo.minBet)
      .toFixed(precision)
      .toString()
    stake === "" && setstake(newStake)
  }, [stake, gameInfo, precision])

  const pages = enableLoading
    ? []
    : ["settings", "Game Rules", "Provably Fair", "History", "SetStake"]
  const [arePagesLoaded, duration] = useLoading(handleAbove, pages)

  const handleStake = (s: string) => {
    setstake(s)
  }

  const handleAudio = (s: any) => {
    setaudio(s)
    sendSettings(stake, s)
  }

  const handleErrorText = (text: string) =>
    seterrorText((errorText) => (errorText ? errorText : text))

  // SAVE SETTINGS
  const saveStake = () => {
    sendSettings(stake, audio)
  }

  const sendSettings = (stakeFromSettings: string, audioFromSettings: any) => {
    const sendstake = stakeFromSettings ? stakeFromSettings : stake
    const sendaudio = audioFromSettings ? audioFromSettings : audio
    const stringified = objToStringSettings(
      {
        stake: [sendstake],
        minStake: minBet,
        maxStake: maxBet,
        sound: sendaudio.sound,
        music: sendaudio.music,
        volume: sendaudio.volume,
      },
      settings
    )
    sendSocketMessage(Protocol.USER_SETTINGS_POST, stringified)
  }

  const receiveSettings = (str: string) => {
    const [stake, music, sound, volume] = settingsStringParsing(str, settings)
    setstake(stake)
    setaudio({
      music,
      sound,
      volume,
    })
  }

  const [
    notification,
    handleNotification,
    notificationfadeOut,
  ] = useNotification()

  const [setStatus, doMessage, sendSocketMessage] = useSocketMessages({
    socket,
    MessagesList,
    setgameInfo,
    addInHistory,
    setSeeds,
    setStateRound,
    handleErrorText,
    receiveSettings,
    setBetReply,
    handleNotification,
    setErrorCode,
  })

  if (!socket.current) {
    createSocket(socket, doMessage, setStatus, WS_URL, name, wsbridge
        // ,"wss://demo.orbitalgaming.com/websocketbridge/ws/videopoker"
    )
  }

  const {
    canGamble,
    showGamble,
    isClickable,
    showGambleResult,
    gameIsOn,
  } = useGameStates(stateRound)

  const SFXS = useGlobalAudio(audio.volume, audio.sound)
  useEffect(() => {
    setisLoaded(arePagesLoaded && !!gameInfo.user_id)
  }, [arePagesLoaded, gameInfo.user_id, setisLoaded])

  useEffect(() => addMouseFlow(), [])

  const requestBetReply = (id: string) => {
    sendSocketMessage(Protocol.GET_ROUND, id)
  }
  const seedRequest = () => {
    sendSocketMessage(Protocol.GET_SEEDS)
  }
  const newSeedRequest = (s: string) => {
    sendSocketMessage(Protocol.SET_SEED, s)
  }

  const verifierFunc = () => {
    window.open("https://codepen.io/orbitalgaming/pen/JjzKQwp", "_blank")
  }

  useEffect(() => {
    console.log(
      "%cV.2.0.2 DG",
      "color: pink; background: black; padding: 5px; border-radius: 8px;"
    )
  }, [])

  return (
    <AppContainer color={colors.primary}>
      <GlobalStyle />
      <Main blur={blur}>
        <Header
          title={"video poker"}
          gameInfo={gameInfo}
          handleAbove={handleAbove}
          colors={{ ...colors, tertiary: colors.secondaryBackground }}
          betInfoBalance={stateRound.balance}
          sfx={SFXS.goForwardSFX}
          fontSize="20px"
          letterSpace="1px"
          balanceUpdateOn={balanceUpdateOn}
        />
        <VideoPoker
          stateRound={stateRound}
          colors={colors}
          handleAbove={handleAbove}
          stake={stake}
          gameInfo={gameInfo}
          sendSocketMessage={sendSocketMessage}
          holded={holded}
          setHolded={setHolded}
          canGamble={canGamble}
          showGamble={showGamble}
          isClickable={isClickable}
          notification={notification}
          handleStake={handleStake}
          sendSettings={saveStake}
          roundId={roundId}
          audio={audio}
          audioUrls={LOCALAUDIO}
          errorMSG={errorText}
          handleNotification={handleNotification}
          isLoaded={isLoaded}
          errorCode={errorCode}
        />
      </Main>
      {above === "settings" && (
        <Settings
          clickable={clickable}
          unBlur={settingsUnblur}
          colors={{ ...colors, tertiary: colors.primary }}
          handleAbove={handleAbove}
          handleAudio={handleAudio}
          audio={audio}
        />
      )}
      {above === "Game Rules" && (
        <GameRules audio={audio} colors={colors} handleAbove={handleAbove}>
          <PokerRules colors={colors} gameInfo={gameInfo} />
        </GameRules>
      )}
      {above === "Provably Fair" && (
        <ProvablyFair
          audio={audio}
          seeds={seeds}
          seedRequest={seedRequest}
          newSeedRequest={newSeedRequest}
          handleNotification={handleNotification}
          raundIsOn={gameIsOn}
          handleAbove={handleAbove}
          colors={{ ...colors, tertiary: colors.secondaryBackground }}
          verifierFunc={verifierFunc}
        />
      )}
      {above === "History" && (
        <History
          unBlur={unBlur}
          audio={audio}
          history={history}
          user_id={user_id}
          handleNotification={handleNotification}
          requestBetReply={requestBetReply}
          setRoundId={setRoundId}
          handleAbove={handleAbove}
          colors={colors}
          toFix={precision}
          payout={true}
        />
      )}
      {above === "SetStake" && (
        <SetStake
          stake={(+stake).toFixed(precision)}
          setStalke={true}
          handleStake={handleStake}
          handleNotification={handleNotification}
          sfx={SFXS.goForwardSFX}
          audio={audio}
          gameInfo={gameInfo}
          handleAbove={handleAbove}
          colors={colors}
          size={"23px"}
        />
      )}
      {showGamble && (
        <Gamble
          stateRound={stateRound}
          gameInfo={gameInfo}
          sendSocketMessage={sendSocketMessage}
          showGambleResult={showGambleResult}
          audioUrl={LOCALAUDIO}
          colors={colors}
          handleAbove={handleAbove}
          handleNotification={handleNotification}
        />
      )}
      {above === "Bet Reply" && (
        <BetReply
          handleNotification={handleNotification}
          betReplyResponse={betReply}
          roundId={roundId}
          audio={audio}
          toFix={precision}
          handleAbove={handleAbove}
          colors={{
            ...colors,
            tertiary: colors.primary,
            closeStrokeColor: colors.primary,
          }}
          headerpadding="30px"
        >
          <CustomReply colors={colors} betReplyResponse={betReply} />
        </BetReply>
      )}
      {!isLoaded && !enableLoading && <Loading duration={duration} />}
      {!!notification && (
        <Notification
          colors={{ ...colors, tertiary: colors.primary }}
          fadeOut={notificationfadeOut}
          handleNotification={handleNotification}
          text={notification}
          unBlur={unBlur}
          audio={audio}
        />
      )}
      {!!errorText && <Error text={errorText} unBlur={false} audio={audio} />}
    </AppContainer>
  )
}

export default App
